import React from 'react';

import { Photo, PhotoRow } from '../photos.component';

// The story, in English. Every other story.<language>.jsx here is the same
// story translated, with the photographs in the same places.

const page = {
    title: 'The Story behind CaptiVision',
    eyebrow: 'The story',
    lede: 'How the idea for CaptiVision was born in a classroom at the University of Texas at Austin.',
    body: <>
        <p className="cv-first">
            In 2025, Tewodros (Teddy) Wondimu Robi was selected for the Mandela Washington Fellowship, the
            flagship program of the U.S. Government’s Young African Leaders Initiative (YALI). Teddy was
            hosted at the University of Texas at Austin alongside a cohort of accomplished leaders. That is
            where he met Mamy Sira, a deaf business owner and disability advocate from The Gambia.  Attending
            classes directly behind Mamy Sira, Teddy learned about the challenges Deaf and Hard of Hearing
            individuals encounter when attending regular lectures and meetings.
        </p>

        <PhotoRow layout="2-1">
            <Photo name="group" alt="The Mandela Washington Fellowship cohort at the University of Texas at Austin, standing together on the steps of a campus building, many wearing stoles in their national colours." />
            <Photo name="seal" alt="Seal of The University of Texas at Austin" />
        </PhotoRow>

        <p>
            The University of Texas at Austin provided extensive accommodations for the program, including American Sign
            Language (ASL) interpreters, desktop microphones, and real-time captioning software. However, during intensive
            lectures, Teddy noticed a persistent struggle. To follow discussions, Mamy Sira had to repeatedly shift her
            focus between the lecture slides, the live ASL interpreter, the laptop screen displaying captions, and
            whichever participant was speaking across the room. Tracking dynamic classroom dialogue required continuous
            visual scanning, which resulted in unnecessary fatigue and neck pain during an already demanding six-week course.
        </p>
        <p>
            Teddy envisioned a system capable of integrating all visual cues. He discussed the concept with Mamy Sira and ASL
            interpreters Aisha S. Terp and Dr. Saint Hardy, subsequently obtaining permission from Prof. Rodney Northern to
            conduct a live trial. Utilizing borrowed equipment from the McCombs School of Business AV support desk, Teddy assembled
            a rudimentary prototype employing a laptop, an iPhone, a USB webcam, and Zoom. The initial version was rudimentary:
            the quality was subpar, the captioning software and Zoom were separate applications, and navigating multiple Zoom
            windows to locate the speaker remained challenging, yet the fundamental function was evident. Aisha remarked that
            it was how every class session should have been delivered from the start.
        </p>

        <PhotoRow>
            <Photo name="zoomCall" alt="The first prototype on Zoom: two camera views of the classroom side by side, one from an iPhone and one from a laptop webcam." />
        </PhotoRow>
        <PhotoRow layout="3">
            <Photo name="aisha" alt="Aisha, an ASL interpreter, at the front of the classroom beside the lecture slides." />
            <Photo name="iphone" alt="An iPhone on a tall stand at the front of the classroom, set up as a camera for the prototype." />
            <Photo name="mamySiraWebcam" alt="Mamy Sira in class, with a laptop and a webcam on a tripod set up in front of the whiteboard." />
        </PhotoRow>

        <p>
            Upon returning to Ethiopia, Teddy embarked on the development of a standalone platform named
            CaptiVision. Translating the prototype into a dependable application necessitated overcoming
            intricate engineering challenges: integrating computer vision, machine learning, active-speaker
            detection, peer-to-peer networking, facial recognition, on-device captioning, speaker diarization,
            and multi-stream video and audio processing.
        </p>

        <PhotoRow layout="half">
            <Photo name="prototype" alt="Teddy and Mamy Sira taking a selfie, next to a laptop in the lecture hall showing the prototype's camera views on Zoom." />
        </PhotoRow>

        <p>
            After months of development and overcoming several points of near-burnout, Teddy completed the
            initial build. CaptiVision delivers a unified visual stream designed to eliminate split focus for
            Deaf and Hard of Hearing individuals in classrooms and meeting spaces, with ongoing efforts
            underway to make the app entirely free for K-12 students worldwide.
        </p>

        <PhotoRow layout="half">
            <Photo name="teddyAndMamySira" alt="Teddy and Mamy Sira smiling together in the classroom." />
        </PhotoRow>
    </>,
    closing: {
        title: 'See what it became.',
        how: 'See how it works',
        start: 'Get started'
    }
}

export default page;
